import { useEffect, useState } from "react";
import { Loader2, Calculator, TrendingDown, CalendarClock, ShieldCheck, AlertTriangle, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { hoyMX, useReversionFechaAnterior } from "@/lib/fechaPago";
import { toast } from "sonner";

const OPCIONES = [
  {
    key: "bajar_mensualidad", icon: TrendingDown, titulo: "Bajar mensualidad",
    desc: "Mismo plazo: los pagos siguientes son más chicos.",
  },
  {
    key: "acortar_plazo", icon: CalendarClock, titulo: "Acortar plazo",
    desc: "Misma mensualidad: el crédito termina antes.",
  },
];

export const PrepagoCapital = ({ credito, pagoBase, esMigrado = false, onSetMonto, onDone }) => {
  const [sim, setSim] = useState(null);
  const [modalidad, setModalidad] = useState("");
  const [busy, setBusy] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [motivo, setMotivo] = useState("");

  const fechaPago = pagoBase.fecha_pago || hoyMX();
  const fechaAnterior = fechaPago < hoyMX();
  // Build G: qué se revertirá y si el crédito viene de Migración de cartera (aunque no tenga pagos históricos).
  const rev = useReversionFechaAnterior(credito?.id, fechaPago, { tipo: "abono" });
  const migrado = esMigrado || credito?.migrado || credito?.origen_alta === "migracion_cartera" || !!rev?.es_migrado;
  const bloqueadoNoMigrado = fechaAnterior && rev !== null && !migrado;
  const requiereMotivo = fechaAnterior && migrado;
  const motivoInvalido = requiereMotivo && motivo.trim().length < 10;
  const saldo = Number(credito?.saldo_insoluto || 0);

  // Reinicia la vista previa si cambian monto o fecha del pago (Build F).
  useEffect(() => { setSim(null); setModalidad(""); }, [pagoBase.monto, pagoBase.fecha_pago]);

  const simular = async () => {
    const monto = Number(pagoBase.monto || 0);
    if (!monto || monto <= 0) { toast.error("Captura el monto del abono a capital"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/creditos/${credito.id}/prepago/simular`, {
        monto, fecha_pago: fechaPago, motivo,
      });
      setSim(data);
      if (data.liquida) setModalidad("bajar_mensualidad");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo calcular la vista previa");
    } finally { setBusy(false); }
  };

  const aplicar = async () => {
    if (!modalidad) { toast.error("Elige cómo quiere el cliente aplicar el abono"); return; }
    if (motivoInvalido) { toast.error("Captura el motivo del abono con fecha anterior (mínimo 10 caracteres)"); return; }
    setAplicando(true);
    try {
      const { data } = await api.post(`/creditos/${credito.id}/prepago/aplicar`, {
        monto: Number(pagoBase.monto), modalidad,
        fecha_pago: fechaPago, motivo, medio_pago: pagoBase.medio_pago,
        banco: pagoBase.banco, referencia: pagoBase.referencia,
        pago_efectivo: pagoBase.pago_efectivo, pago_tercero: pagoBase.pago_tercero,
        nombre_tercero: pagoBase.nombre_tercero,
      });
      toast.success(data.credito.estatus === "Liquidado"
        ? "Abono aplicado: el crédito quedó LIQUIDADO."
        : "Abono a capital aplicado. Tabla de amortización recalculada.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar el abono a capital");
    } finally { setAplicando(false); }
  };

  return (
    <div className="space-y-3" data-testid="prepago-capital">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <b>Pago anticipado a capital.</b> Baja el saldo insoluto y recalcula el crédito
        (derecho del cliente — Circular 16/2007 Banxico). Antes de confirmar verás cómo quedan los pagos.
      </div>

      {/* Saldo insoluto a la fecha del pago + liquidar total (Build F) */}
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
        data-testid="prepago-saldo-fecha">
        <div className="text-xs text-slate-600">
          <span className="flex items-center gap-1.5 font-medium text-slate-700">
            <Wallet className="h-3.5 w-3.5 text-primary" /> Saldo insoluto al {fdate(fechaPago)}
          </span>
          <span className="font-mono text-sm font-semibold tabular-nums text-slate-800">{mxn(saldo)}</span>
        </div>
        <Button type="button" variant="outline" size="sm" data-testid="prepago-liquidar-total-btn"
          disabled={bloqueadoNoMigrado || saldo <= 0}
          onClick={() => { onSetMonto?.(String(saldo)); }}
          className="border-primary/40 text-primary">
          Liquidar total
        </Button>
      </div>

      {/* Crédito NO migrado con fecha anterior: no se permite (punto 2c) */}
      {bloqueadoNoMigrado && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-800"
          data-testid="prepago-bloqueo-no-migrado">
          <AlertTriangle className="mr-1.5 inline h-4 w-4" />
          El abono a capital con fecha anterior solo aplica a créditos migrados.
        </div>
      )}

      {/* Crédito migrado con fecha anterior: aviso + motivo obligatorio */}
      {requiereMotivo && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5"
          data-testid="prepago-aviso-fecha-anterior">
          <p className="text-sm font-medium text-amber-900">
            <CalendarClock className="mr-1.5 inline h-4 w-4" />
            Pago con fecha anterior: el crédito se evalúa al {fdate(fechaPago)}.
          </p>
          {rev?.aviso && (
            <p className="text-xs font-medium text-amber-900" data-testid="prepago-aviso-reversion">
              <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5" />{rev.aviso}
            </p>
          )}
          <div>
            <Label className="mb-1 block text-xs text-amber-900">Motivo (obligatorio, mínimo 10 caracteres)</Label>
            <Textarea data-testid="prepago-motivo" rows={2} value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Explica por qué se registra con fecha anterior" className="bg-white text-sm" />
            {motivoInvalido && (
              <p className="mt-1 text-[11px] text-red-600" data-testid="prepago-motivo-error">
                Faltan {Math.max(0, 10 - motivo.trim().length)} caracteres.
              </p>
            )}
          </div>
        </div>
      )}

      {!sim && !bloqueadoNoMigrado && (
        <Button data-testid="prepago-simular-btn" onClick={simular} disabled={busy}
          variant="outline" className="w-full border-primary/40 text-primary">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />}
          Ver vista previa del recálculo
        </Button>
      )}

      {sim && (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["Saldo actual", mxn(sim.saldo_actual)], ["Abono a capital", mxn(sim.monto)],
              ["Nuevo saldo", mxn(sim.saldo_nuevo)]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                <p className="text-sm font-semibold tabular-nums text-slate-800">{v}</p>
              </div>
            ))}
          </div>

          {sim.liquida ? (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="prepago-liquida">
              <ShieldCheck className="mr-1.5 inline h-4 w-4" />
              Este abono <b>liquida el crédito por completo</b>. No quedarán cuotas pendientes.
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-600">El cliente elige cómo aplicar el abono:</p>
              {OPCIONES.map((op) => {
                const o = sim.opciones[op.key];
                const activa = modalidad === op.key;
                const Icono = op.icon;
                return (
                  <button key={op.key} type="button" onClick={() => setModalidad(op.key)}
                    data-testid={`prepago-opcion-${op.key}`}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${activa ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Icono className="h-4 w-4 text-primary" /> {op.titulo}
                      </span>
                      <span className={`h-3.5 w-3.5 rounded-full border ${activa ? "border-primary bg-primary" : "border-slate-300"}`} />
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500">{op.desc}</p>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <p className="text-slate-400">Pago{sim.sistema === "alemana" ? " (1a cuota)" : ""}</p>
                        <p className="font-mono font-semibold text-slate-700">
                          {mxn(sim.pago_actual)} → <span className="text-primary">{mxn(o.pago_nuevo)}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-400">Cuotas restantes</p>
                        <p className="font-mono font-semibold text-slate-700">
                          {sim.cuotas_restantes} → <span className="text-primary">{o.plazo_restante}</span>
                          {o.cuotas_eliminadas > 0 && <span className="ml-1 text-emerald-600">(-{o.cuotas_eliminadas})</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-400">Ahorro de intereses</p>
                        <p className="font-mono font-semibold text-emerald-600">{mxn(o.ahorro_intereses)}</p>
                      </div>
                    </div>
                    <p className="mt-1 text-[10px] text-slate-400">Último pago: {fdate(o.fecha_ultimo_pago)}</p>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" disabled={busy || aplicando}
              onClick={() => { setSim(null); setModalidad(""); }} data-testid="prepago-recalcular-btn">
              Recalcular
            </Button>
            <Button size="sm" className="flex-1 bg-primary hover:bg-sidebar-hover"
              disabled={!modalidad || aplicando || motivoInvalido}
              onClick={aplicar} data-testid="prepago-confirmar-btn">
              {aplicando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmar abono a capital
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
